// The row picker's page count, for one report page: plans the real rows
// with the document's own spec and the pdf chunk's text measurer, so the
// number the picker shows is the number of pages the PDF will have.
//
// `prepare` runs when the picker opens: it loads the measurer (the lazy pdf
// chunk plus the embedded fonts, which the download needs anyway) and, for a
// report whose download fetches more rows than the page holds, that full
// row set too, so the plan is made on the rows the PDF will print. Until it
// resolves the picker shows "…". If the fetch fails the plan falls back to
// the loaded rows and the count is marked approximate; if the measurer
// fails to load there is no count at all (the download reports its own
// error). `prepare` never rejects.
import { useCallback, useMemo, useRef, useState } from "react";
import { DocSpec, createDocPlanner } from "pdf/pagePlan";
import { TextMeasurer } from "pdf/textMetrics";
import { TableSortState } from "shared/types/tableSort";
import { loadReportPdf } from "shared/utils/loadPdfChunk";

export type PageCountFor = (rows: number) => { pages: number; exact: boolean } | null;

export const useReportPageCount = <Data, Row extends Record<string, any>>(
  spec: DocSpec<Data, Row>,
  data: Data | undefined,
  sortState: TableSortState | undefined,
  options?: {
    // When the download will fetch a bigger row set than `data` holds, how
    // to fetch it (the same call the download makes); the result is kept
    // for the download too.
    loadAll?: () => Promise<Data | undefined>;
    needsAll?: boolean;
  },
) => {
  const [measurer, setMeasurer] = useState<TextMeasurer | null>(null);
  // The full row set, keyed by the `data` it was fetched for, so a new
  // window (a new query result) fetches again and a stale set is never
  // planned. A sort change keeps it: sorting is applied client-side.
  const [full, setFull] = useState<{ for: Data; data: Data } | null>(null);
  // One load at a time per data set; a second open while the first is in
  // flight joins it, an open for a different data set starts its own.
  const inFlight = useRef<{ for: Data | undefined; promise: Promise<void> } | null>(null);

  const needsAll = !!options?.needsAll && !!options.loadAll;
  const fullData = full && full.for === data ? full.data : null;
  const source = fullData ?? data;

  const prepare = useCallback(async () => {
    const pending = inFlight.current;
    if (pending && pending.for === data) return pending.promise;
    const entry: { for: Data | undefined; promise: Promise<void> } = {
      for: data,
      promise: Promise.resolve(),
    };
    // Installed before the body runs, so the guards below always see it even
    // when nothing in the body suspends.
    inFlight.current = entry;
    entry.promise = (async () => {
      try {
        if (!measurer) {
          const { loadReportMeasurer } = await loadReportPdf();
          setMeasurer(await loadReportMeasurer());
        }
        if (needsAll && data && fullData === null && options?.loadAll) {
          const loaded = await options.loadAll();
          // A load for a window the user has already left must not publish
          // its rows over the newer one's.
          if (loaded && inFlight.current === entry) setFull({ for: data, data: loaded });
        }
      } catch {
        // No count rather than a wrong one; the download has its own error path.
      } finally {
        if (inFlight.current === entry) inFlight.current = null;
      }
    })();
    return entry.promise;
  }, [measurer, needsAll, data, fullData, options]);

  const planner = useMemo(
    () => (measurer && source ? createDocPlanner(spec, source, sortState, measurer) : null),
    [spec, source, sortState, measurer],
  );

  const pagesFor: PageCountFor = useCallback(
    (rows: number) => {
      if (!planner) return null;
      const result = planner.estimate(rows);
      // Plans made on the loaded rows while the download will fetch more are
      // approximate even when every requested row is in hand.
      const exact = result.exact && (!needsAll || fullData !== null);
      return { pages: result.pages, exact };
    },
    [planner, needsAll, fullData],
  );

  return { prepare, pagesFor, source };
};
